import { useState } from 'react';
import { captureClaimsAwaitingMine, captureSignClaim, type ApiError } from '@aithora/shared';
import { useSession } from '../session/SessionContext';
import { useQuery } from '../useQuery';
import { useReadOnly } from './ReadOnly';
import { ConfirmDialog } from './ConfirmDialog';
import { sx } from './designStyles';

const money = (cents: number) => '$' + (cents / 100).toFixed(2);

/**
 * The claims sent to the signed-in practitioner for signature (their NPI), each signed
 * from their own login with the attestation (POST /capture/claims/:id/sign). Nothing is
 * shown to anyone without claims waiting. It sits where that person works: the Worklist
 * for a practitioner on care staff, the Capture claims tab for one with a desk role.
 */
export function ClaimsToSign() {
  const { client } = useSession();
  const readOnly = useReadOnly();
  const q = useQuery((signal) => captureClaimsAwaitingMine(client, { signal }), [client], { enabled: !readOnly });
  const [signing, setSigning] = useState<{ id: string; label: string } | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);
  const claims = q.data?.claims ?? [];
  if (readOnly || !claims.length) return null;
  return (
    <section data-testid="claims-to-sign" aria-label="Claims awaiting your signature" style={sx('box-sizing:border-box;border:1px solid rgba(47,92,151,.35);border-radius:10px;background:rgba(79,138,214,.07);padding:10px 14px;margin-bottom:12px;display:flex;flex-direction:column;gap:6px;')}>
      <div style={sx('font-size:14px;font-weight:800;color:#22272E;')}>
        {`Claims awaiting your signature · ${claims.length}`}
        <span style={sx('font-size:14px;font-weight:400;color:#54606C;margin-left:8px;')}>{`under NPI ${q.data?.npi ?? ''}`}</span>
      </div>
      {claims.map((c) => {
        const label = `${c.resident ?? 'Resident'} · ${c.cptCode} · ${c.period}`;
        return (
          <div key={c.id} style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;')}>
            <span style={sx('flex:1 1 220px;min-width:0;font-size:13px;color:#22272E;')}>{label}</span>
            <span style={sx('font-size:13px;font-weight:700;font-variant-numeric:tabular-nums;')}>{money(c.amountCents)}</span>
            <button type="button" onClick={() => setSigning({ id: c.id, label })} style={sx('height:30px;padding:0 13px;border-radius:8px;border:0;background:#2F5C97;color:#fff;font-size:13px;font-weight:600;cursor:pointer;')}>
              Sign
            </button>
          </div>
        );
      })}
      {err && <div role="alert" style={sx('font-size:16px;color:#B3261E;')}>{err.message}</div>}
      {signing && (
        <ConfirmDialog
          title={`Sign the claim for ${signing.label}?`}
          body="I reviewed this claim and the services were provided as documented. Your signature, NPI and the time are sealed to the record; Aithora sends it only as your billing agent."
          confirmLabel="Sign claim"
          onConfirm={async () => {
            setErr(null);
            await captureSignClaim(client, { params: { id: signing.id }, body: { attest: true } });
            q.reload();
          }}
          onClose={() => setSigning(null)}
        />
      )}
    </section>
  );
}
